export const isValidHex = (value = '') => /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.test(String(value).trim());

export const isFullHex = (value = '') => /^#([0-9a-f]{6})$/i.test(String(value).trim());

export const normalizePalette = (palette = []) => {
  if (!Array.isArray(palette)) return [];
  return palette
    .map((color, index) => {
      if (typeof color === 'string') {
        const colorText = color.trim();
        const isHex = isValidHex(colorText);
        return {
          hex: isHex ? colorText : '',
          name: isHex ? `Color ${index + 1}` : colorText
        };
      }
      return {
        hex: isValidHex(color?.hex) ? color.hex.trim() : '',
        name: color?.name || `Color ${index + 1}`
      };
    })
    .slice(0, 6);
};

export const getSavedPalette = (palette = []) => normalizePalette(palette)
  .filter((color) => isValidHex(color.hex))
  .map((color) => ({
    hex: color.hex.trim(),
    name: String(color.name || '').trim()
  }));

const clampChannel = (value) => Math.max(0, Math.min(255, Math.round(value)));

const adjustHex = (hex, { lighten = 0, darken = 0 } = {}) => {
  const raw = String(hex || '').replace('#', '').trim();
  if (!/^[0-9a-f]{6}$/i.test(raw)) return null;
  const shift = lighten > 0 ? lighten : -darken;
  const channels = [0, 2, 4].map((offset) => {
    const base = parseInt(raw.slice(offset, offset + 2), 16);
    return clampChannel(base + shift * 255);
  });
  return `#${channels.map((channel) => channel.toString(16).padStart(2, '0')).join('')}`;
};

const FALLBACK_SUGGESTED_COLORS = [
  { name: 'Warm ivory', hex: '#FFF7ED' },
  { name: 'Sage', hex: '#9CAF88' },
  { name: 'Dusty blue', hex: '#7DA3C7' },
  { name: 'Terracotta', hex: '#C86B4A' }
];

/** Theme-aware suggestions derived from the current palette (with safe fallbacks). */
export const deriveSuggestedColors = (palette = []) => {
  const saved = getSavedPalette(palette);
  if (!saved.length) return FALLBACK_SUGGESTED_COLORS;

  const seen = new Set();
  const suggestions = [];

  const pushColor = (color) => {
    const hex = String(color?.hex || '').trim().toLowerCase();
    if (!isValidHex(hex) || seen.has(hex)) return;
    seen.add(hex);
    suggestions.push({
      hex,
      name: String(color?.name || '').trim() || 'Accent'
    });
  };

  saved.forEach((color) => {
    pushColor(color);
    const lighter = adjustHex(color.hex, { lighten: 0.12 });
    const darker = adjustHex(color.hex, { darken: 0.1 });
    if (lighter) pushColor({ hex: lighter, name: `${color.name} light` });
    if (darker) pushColor({ hex: darker, name: `${color.name} deep` });
  });

  return suggestions.slice(0, 8);
};
